import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as d3 from 'd3';

export type HourlyData = { hour: number; value: number };

type TooltipState = {
  x: number;
  y: number;
  hour: number;
  value: number;
  pct: number;
  visible: boolean;
};

export type PulseTimelineProps = {
  data: HourlyData[];
  selectedHour?: number;
  onHourChange?: (hour: number) => void;
  hourStart?: number; // default 6
  hourEnd?: number;   // default 21
  isLoading?: boolean;
};

const MARGIN = { top: 24, right: 16, bottom: 32, left: 36 };
const HEIGHT = 180;

// Realistic gym traffic mock data: morning + evening peaks
export const MOCK_HOURLY_DATA: HourlyData[] = [
  { hour: 0, value: 0 }, { hour: 1, value: 0 }, { hour: 2, value: 0 },
  { hour: 3, value: 0 }, { hour: 4, value: 0 }, { hour: 5, value: 3 },
  { hour: 6, value: 18 }, { hour: 7, value: 34 }, { hour: 8, value: 42 },
  { hour: 9, value: 28 }, { hour: 10, value: 15 }, { hour: 11, value: 12 },
  { hour: 12, value: 10 }, { hour: 13, value: 8 }, { hour: 14, value: 11 },
  { hour: 15, value: 14 }, { hour: 16, value: 22 }, { hour: 17, value: 38 },
  { hour: 18, value: 51 }, { hour: 19, value: 48 }, { hour: 20, value: 35 },
  { hour: 21, value: 20 }, { hour: 22, value: 8 }, { hour: 23, value: 2 }
];

function formatHour(h: number): string {
  if (h === 0) return '12 AM';
  if (h < 12) return `${h} AM`;
  if (h === 12) return '12 PM';
  return `${h - 12} PM`;
}

// Interpolates green → yellow → red based on normalized value 0-1
function occupancyColor(t: number, opacity = 1): string {
  let r, g, b;
  if (t < 0.5) {
    const s = t * 2;
    r = Math.round(34 + s * (234 - 34));
    g = Math.round(197 + s * (179 - 197));
    b = Math.round(94 + s * (8 - 94));
  } else {
    const s = (t - 0.5) * 2;
    r = Math.round(234 + s * (239 - 234));
    g = Math.round(179 + s * (68 - 179));
    b = Math.round(8 + s * (68 - 8));
  }
  return `rgba(${r},${g},${b},${opacity})`;
}

export default function PulseTimeline({ data, selectedHour, onHourChange, hourStart = 6, hourEnd = 21, isLoading = false }: PulseTimelineProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [tooltip, setTooltip] = useState<TooltipState>({ x: 0, y: 0, hour: 0, value: 0, pct: 0, visible: false });
  const [width, setWidth] = useState(600);

  // Mock data is only a loading skeleton — never a stand-in for real "today"
  // numbers. Once loaded with no check-ins we show an honest empty state.
  const displayData = useMemo(() => (isLoading ? MOCK_HOURLY_DATA : data), [data, isLoading]);
  const isEmpty = !isLoading && data.length === 0;
  const filteredData = useMemo(() => displayData.filter(d => d.hour >= hourStart && d.hour <= hourEnd), [displayData, hourStart, hourEnd]);
  const maxVal = useMemo(() => Math.max(...displayData.map(d => d.value), 1), [displayData]);

  useEffect(() => {
    const obs = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(Math.max(w, 280));
    });
    if (containerRef.current) obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  const draw = useCallback(() => {
    if (!svgRef.current || filteredData.length === 0) return;

    const EFFECTIVE_START = hourStart;
    const EFFECTIVE_END = hourEnd;

    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = HEIGHT - MARGIN.top - MARGIN.bottom;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('width', width).attr('height', HEIGHT);

    const defs = svg.append('defs');

    // Glow filter for the main line
    const glow = defs.append('filter').attr('id', 'pt-glow').attr('x', '-20%').attr('y', '-50%').attr('width', '140%').attr('height', '200%');
    glow.append('feGaussianBlur').attr('stdDeviation', '3').attr('result', 'blur');
    const merge = glow.append('feMerge');
    merge.append('feMergeNode').attr('in', 'blur');
    merge.append('feMergeNode').attr('in', 'SourceGraphic');

    // Value-based horizontal gradient for fill (green→yellow→red per data density)
    const fillGrad = defs.append('linearGradient')
      .attr('id', 'pt-fill')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '100%').attr('y2', '0%');

    filteredData.forEach(d => {
      const xPct = ((d.hour - EFFECTIVE_START) / (EFFECTIVE_END - EFFECTIVE_START)) * 100;
      const norm = maxVal > 0 ? d.value / maxVal : 0;
      fillGrad.append('stop')
        .attr('offset', `${xPct}%`)
        .attr('stop-color', isLoading ? `rgba(161, 161, 170, ${0.1 + norm * 0.2})` : occupancyColor(norm, 0.55));
    });

    // Separate fade-out gradient for bottom of fill
    const fadeGrad = defs.append('linearGradient')
      .attr('id', 'pt-fade')
      .attr('x1', '0%').attr('y1', '0%')
      .attr('x2', '0%').attr('y2', '100%');
    fadeGrad.append('stop').attr('offset', '0%').attr('stop-color', 'white').attr('stop-opacity', 1);
    fadeGrad.append('stop').attr('offset', '100%').attr('stop-color', 'white').attr('stop-opacity', 0.05);

    // Combine gradients via multiply filter (approximated by stacking rects with clip)
    defs.append('clipPath').attr('id', 'pt-area-clip').append('rect')
      .attr('width', innerW).attr('height', innerH);

    const g = svg.append('g').attr('transform', `translate(${MARGIN.left},${MARGIN.top})`);

    // Scales
    const x = d3.scaleLinear().domain([EFFECTIVE_START, EFFECTIVE_END]).range([0, innerW]);
    const y = d3.scaleLinear().domain([0, maxVal * 1.15]).range([innerH, 0]);

    // Grid lines (subtle)
    const yTicks = y.ticks(3);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter().append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0).attr('x2', innerW)
      .attr('y1', d => y(d)).attr('y2', d => y(d))
      .attr('stroke', 'rgba(0,0,0,0.05)')
      .attr('stroke-width', 1);

    // Selected hour highlight band
    if (selectedHour !== undefined && selectedHour >= EFFECTIVE_START && selectedHour <= EFFECTIVE_END) {
      const bw = x(EFFECTIVE_START + 1) - x(EFFECTIVE_START);
      g.append('rect')
        .attr('x', x(selectedHour) - bw / 2)
        .attr('y', 0)
        .attr('width', bw)
        .attr('height', innerH)
        .attr('fill', '#AAD816')
        .attr('opacity', 0.1)
        .attr('rx', 4);

      // Vertical marker line
      g.append('line')
        .attr('x1', x(selectedHour)).attr('x2', x(selectedHour))
        .attr('y1', 0).attr('y2', innerH)
        .attr('stroke', '#AAD816')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '4,3')
        .attr('opacity', 0.6);
    }

    // Area generators
    const area = d3.area<HourlyData>()
      .x(d => x(d.hour))
      .y0(innerH)
      .y1(d => y(d.value))
      .curve(d3.curveBasis);

    const line = d3.line<HourlyData>()
      .x(d => x(d.hour))
      .y(d => y(d.value))
      .curve(d3.curveBasis);

    // Area fill group (two rects with clip achieve the combined gradient)
    const areaGroup = g.append('g').attr('clip-path', 'url(#pt-area-clip)');

    // Bottom rect: colored gradient fill
    areaGroup.append('path')
      .datum(filteredData)
      .attr('fill', 'url(#pt-fill)')
      .attr('d', area)
      .attr('opacity', 0)
      .transition().duration(800).attr('opacity', 1);

    // Main line
    const linePath = g.append('path')
      .datum(filteredData)
      .attr('fill', 'none')
      .attr('stroke', isLoading ? '#d4d4d8' : '#AAD816')
      .attr('stroke-width', 2)
      .attr('filter', isLoading ? null : 'url(#pt-glow)')
      .attr('d', line);

    // Draw-in animation
    const totalLen = (linePath.node() as SVGPathElement)?.getTotalLength() || 0;
    linePath
      .attr('stroke-dasharray', `${totalLen} ${totalLen}`)
      .attr('stroke-dashoffset', totalLen)
      .transition().duration(1400).ease(d3.easeQuadInOut)
      .attr('stroke-dashoffset', 0);

    // Dynamic X-axis ticks — denser for small ranges, sparser for wide ranges
    const rangeSize = EFFECTIVE_END - EFFECTIVE_START;
    const tickStep = rangeSize <= 2 ? 1 : rangeSize <= 6 ? 1 : rangeSize <= 10 ? 2 : 3;
    const tickValues: number[] = [];
    for (let h = EFFECTIVE_START; h <= EFFECTIVE_END; h += tickStep) tickValues.push(h);

    const fmtHour = (d: d3.NumberValue) => {
      const h = d as number;
      if (h === 0 || h === 24) return '12 AM';
      if (h === 12) return '12 PM';
      return h < 12 ? `${h} AM` : `${h - 12} PM`;
    };

    g.append('g')
      .attr('transform', `translate(0,${innerH})`)
      .call(
        d3.axisBottom(x)
          .tickValues(tickValues)
          .tickFormat(fmtHour)
          .tickSize(3)
      )
      .call(ax => ax.select('.domain').attr('stroke', 'rgba(0,0,0,0.1)'))
      .call(ax => ax.selectAll('.tick line').attr('stroke', 'rgba(0,0,0,0.1)'))
      .call(ax => ax.selectAll('text')
        .attr('fill', 'rgba(0,0,0,0.4)')
        .attr('font-size', '9px')
        .attr('font-weight', '700')
        .attr('font-family', 'inherit')
        .attr('dy', '1.4em'));

    // Hover elements (initially hidden)
    const hoverLine = g.append('line')
      .attr('y1', 0).attr('y2', innerH)
      .attr('stroke', 'rgba(0,0,0,0.2)')
      .attr('stroke-width', 1);

    const hoverDot = g.append('circle')
      .attr('r', 5)
      .attr('fill', '#000')
      .attr('stroke', '#AAD816')
      .attr('stroke-width', 2.5)
      .attr('opacity', 0)
      .attr('pointer-events', 'none');

    // Invisible hover overlay
    g.append('rect')
      .attr('width', innerW)
      .attr('height', innerH)
      .attr('fill', 'transparent')
      .style('cursor', 'crosshair')
      .on('mousemove', function(event) {
        const [mx] = d3.pointer(event);
        const rawHour = x.invert(mx);
        const snapped = Math.round(rawHour);
        const hour = Math.max(EFFECTIVE_START, Math.min(EFFECTIVE_END, snapped));
        const pt = filteredData.find(d => d.hour === hour) ?? { hour, value: 0 };

        hoverLine.attr('x1', mx).attr('x2', mx).attr('opacity', 0.6);
        hoverDot.attr('cx', x(pt.hour)).attr('cy', y(pt.value)).attr('opacity', 1);

        const pct = maxVal > 0 ? Math.round((pt.value / maxVal) * 100) : 0;
        setTooltip({ x: mx + MARGIN.left, y: y(pt.value) + MARGIN.top, hour, value: pt.value, pct, visible: true });
        // Removed onHourChange(hour) to prevent re-renders on hover
      })
      .on('mouseleave', () => {
        hoverLine.attr('opacity', 0);
        hoverDot.attr('opacity', 0);
        setTooltip(prev => ({ ...prev, visible: false }));
      });

  }, [filteredData, width, maxVal, selectedHour, onHourChange, hourStart, hourEnd, isLoading]);

  useEffect(() => { draw(); }, [draw]);

  const tooltipColor = tooltip.pct > 66 ? '#ef4444' : tooltip.pct > 33 ? '#eab308' : '#22c55e';

  return (
    <div ref={containerRef} className="relative w-full select-none">
      {isEmpty ? (
        <div className="flex flex-col items-center justify-center text-center" style={{ height: HEIGHT }}>
          <p className="text-sm font-black text-zinc-400 uppercase tracking-wider">No check-ins yet today</p>
          <p className="text-[10px] text-zinc-300 font-bold mt-1">Traffic will appear here as members check in</p>
        </div>
      ) : (
        <svg ref={svgRef} className="w-full overflow-visible" />
      )}

      {/* Tooltip */}
      {!isEmpty && tooltip.visible && (
        <div
          className="absolute z-20 pointer-events-none"
          style={{ left: tooltip.x, top: tooltip.y - 70, transform: 'translateX(-50%)' }}
        >
          <div className="bg-white/95 border border-zinc-200 rounded-xl px-3 py-2 shadow-2xl text-center min-w-[100px] backdrop-blur-md">
            <div className="text-zinc-950 font-black text-sm tabular-nums">{tooltip.value}</div>
            <div className="text-zinc-400 text-[9px] font-bold uppercase tracking-widest">{formatHour(tooltip.hour)}</div>
            <div className="font-bold text-[10px] mt-0.5" style={{ color: tooltipColor }}>{tooltip.pct}% peak</div>
          </div>
          {/* Arrow */}
          <div className="absolute left-1/2 -translate-x-1/2 -bottom-1.5 w-3 h-1.5 overflow-hidden">
            <div className="w-3 h-3 bg-white/95 border-r border-b border-zinc-200 rotate-45 -translate-y-1.5" />
          </div>
        </div>
      )}
    </div>
  );
}
